import type { CrewlinkClient } from '../supabase-client';
import type { ProjectRow, ProjectStatus } from '../database.types';
import { isStaleWriteRefusal } from '../../lib/error-classification';

/**
 * Writer wrapper for `update_project_status` (migration 0003).
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. UI code never calls `.rpc(...)` directly and never
 * calls `.update(...)` at all.
 *
 * Ownership is not a parameter — it comes from `auth.uid()` inside the function
 * (INV-T23). `expectedStatus` is the concurrency guard: the update only lands
 * while the Project is still in that status.
 */
export async function updateProjectStatus(
  client: CrewlinkClient,
  projectId: string,
  expectedStatus: ProjectStatus,
  newStatus: ProjectStatus,
): Promise<ProjectRow> {
  const { data, error } = await client.rpc('update_project_status', {
    p_project_id: projectId,
    p_expected_status: expectedStatus,
    p_new_status: newStatus,
  });

  if (error) throw error;
  if (!data) throw new Error('update_project_status returned no project row');

  return data;
}

/**
 * True when the failure was the stale-write guard rather than anything else.
 *
 * **STRUCTURED FIRST, TEXT ONLY AS A FALLBACK (I-006's fix).** This used to
 * read the English message alone, so rewording a `raise exception` in a future
 * migration would silently have downgraded the specific "changed somewhere
 * else" wording to the generic one. It now prefers PostgREST's structured
 * `code`. It still ACCEPTS a bare message — several callers only have one —
 * and the shared helper keeps the text match beneath the code check, because
 * here a wrong answer costs a less specific sentence and nothing more: the
 * screen re-reads server truth on any failure regardless.
 */
export function isStaleStatusWrite(error: unknown): boolean {
  return isStaleWriteRefusal(error);
}

/**
 * Turn a failure into something Brandon can act on.
 *
 * The stale case gets its own wording because it is the one failure where the
 * honest answer is "someone or something else already changed this" — telling
 * him to simply try again would be wrong, since the screen he is looking at is
 * out of date.
 */
export function describeStatusChangeError(message: string, code?: string): string {
  const normalised = message.toLowerCase();

  if (isStaleStatusWrite({ code, message })) {
    return 'This Project was changed somewhere else. Showing its current status.';
  }
  if (normalised.includes('not found') || normalised.includes('p0002')) {
    return 'That Project is no longer available.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('invalid input value for enum')) {
    return 'That is not a valid status.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not change the status. Please try again.';
}
